"use client";
import { workspaceLayoutSchema } from "../workspacePersistence";
import { isSavedComponentClosed } from "@/src/features/components/reopenComponent";
import { isEphemeralComponentName } from "@/src/features/windows/ephemeralComponents";
import { isRetiredComponentName } from "@/src/features/windows/retiredComponents";
import { collapseSingletonWindows } from "@/src/features/windows/singletonWindows";
import type { WorkspaceSaveDraft, WorkspaceSnapshot } from "./workspaceSaveQueue";

/** Use old recovery results only as candidate IDs, never as authoritative data. */
export function restoredWorkspacePreferences(pages: WorkspaceSnapshot[]) {
  const { layout, collapsed } = restoredWorkspace(pages);
  // Older builds let every opener stack another Settings (or support inbox,
  // or clock) window, and all the saved copies came back on sign-in. Restore
  // only the copy the user saw last.
  return { ...layout, componentsMap: collapsed.kept };
}

/**
 * The write that makes the singleton collapse stick. Dropping a duplicate only
 * from the restored view leaves its saved row open, so a later NeuralCanvas-only
 * layout would revive it through the fallback above. Close each dropped copy
 * and take it out of the saved layout, as closing the window would. Returns
 * null when nothing was collapsed.
 */
export function collapsedSingletonCleanup(pages: WorkspaceSnapshot[], closedAt: string): WorkspaceSaveDraft | null {
  const { layout, stored, collapsed } = restoredWorkspace(pages);
  if (!collapsed.removed.length) return null;
  const removedIds = new Set(collapsed.removed.map(entry => entry.id));
  const components = Array.from(removedIds).flatMap(id => {
    const payload = stored.get(id);
    return payload ? [{ ...payload, id, deletedDate: closedAt }] : [];
  });
  return { components, layout: { ...layout, componentsMap: layout.componentsMap.filter(entry => !removedIds.has(entry.id)) } };
}

function restoredWorkspace(pages: WorkspaceSnapshot[]) {
  if (!pages.length) throw new Error("Workspace snapshot is missing");
  const raw = pages[0].layout.payload.componentsMap;
  const layout = workspaceLayoutSchema.parse({ ...pages[0].layout.payload, componentsMap: typeof raw === "string" ? JSON.parse(raw) : raw });
  const stored = new Map(pages.flatMap(page => page.components).map(value => [value.componentId, value.payload]));
  const ids = layout.componentsMap.map(value => value.id);
  // The compatibility reader already selected recent recovery candidates. The
  // new snapshot rechecks their current payload and closed state before display.
  if (layout.componentsMap.length === 1 && layout.componentsMap[0].name === "NeuralCanvas") {
    for (const [id, payload] of Array.from(stored.entries())) {
      if (!payload || ids.includes(id) || isSavedComponentClosed(payload)
        || typeof payload.name !== "string" || isEphemeralComponentName(payload.name)
        || isRetiredComponentName(payload.name)) continue;
      const size = payload.size as { width?: number; height?: number } | undefined;
      if (!size || !Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width! <= 0 || size.height! <= 0) continue;
      ids.push(id);
    }
  }
  const entries = ids.flatMap(id => {
    const payload = stored.get(id);
    // Legacy layouts can precede a dedicated component row. The established
    // grid hydrator supplies missing defaults, using null for the first save.
    const entry = payload ?? layout.componentsMap.find(value => value.id === id);
    return entry && !isRetiredComponentName(entry.name) ? [entry] : [];
  });
  return { layout, stored, collapsed: collapseSingletonWindows(entries) };
}
